import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { CASE_STATUSES, CASE_TYPES } from "@/lib/helpers";
import { toast } from "sonner";

const empty = {
  case_number: "", title: "", case_type: "", category: "", court: "", court_location: "",
  filing_date: "", status: "active", client_id: "", opposite_party_name: "",
  opposite_party_contact: "", opposite_party_address: "", advocate_id: "",
  description: "", important_facts: "", legal_sections: "", remarks: "",
  next_hearing_date: "", next_hearing_time: "", hearing_purpose: "", court_room: "", hearing_notes: "",
};

function Section({ title, children }) {
  return (
    <div className="space-y-4">
      <h3 className="font-display text-lg text-navy">{title}</h3>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">{children}</div>
    </div>
  );
}

export default function CaseFormDialog({ open, onOpenChange, initial, onSaved }) {
  const [form, setForm] = useState(empty);
  const [clients, setClients] = useState([]);
  const [advocates, setAdvocates] = useState([]);
  const [courts, setCourts] = useState([]);
  const [saving, setSaving] = useState(false);
  const editing = !!initial?.id;

  useEffect(() => {
    if (open) {
      setForm(initial ? { ...empty, ...initial } : empty);
      Promise.all([
        api.get("/clients"), api.get("/advocates"), api.get("/courts"),
      ]).then(([c, a, ct]) => { setClients(c.data); setAdvocates(a.data); setCourts(ct.data); }).catch(() => {});
    }
  }, [open, initial]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target?.value ?? e }));

  const save = async (asDraft) => {
    if (!form.case_number || !form.title) { toast.error("Case number and title are required"); return; }
    setSaving(true);
    try {
      const payload = { ...form, is_draft: asDraft, client_id: form.client_id || null, advocate_id: form.advocate_id || null };
      const res = editing ? await api.put(`/cases/${initial.id}`, payload) : await api.post("/cases", payload);
      toast.success(editing ? "Case updated" : asDraft ? "Draft saved" : "Case created");
      onSaved?.(res.data);
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-navy">{editing ? "Edit Case" : "Add New Case"}</DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-2">
          <Section title="Basic Information">
            <Field label="Case Number *"><Input data-testid="case-number-input" value={form.case_number} onChange={set("case_number")} placeholder="CS/2025/1234" /></Field>
            <Field label="Case Title *"><Input data-testid="case-title-input" value={form.title} onChange={set("title")} placeholder="Party A vs. Party B" /></Field>
            <Field label="Case Type">
              <Select value={form.case_type} onValueChange={set("case_type")}>
                <SelectTrigger data-testid="case-type-select"><SelectValue placeholder="Select type" /></SelectTrigger>
                <SelectContent>{CASE_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Category"><Input value={form.category} onChange={set("category")} placeholder="Original Side / Appeal" /></Field>
            <Field label="Court">
              <Select value={form.court} onValueChange={set("court")}>
                <SelectTrigger data-testid="case-court-select"><SelectValue placeholder="Select court" /></SelectTrigger>
                <SelectContent>{courts.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Court Location"><Input value={form.court_location} onChange={set("court_location")} /></Field>
            <Field label="Filing Date"><Input type="date" value={form.filing_date || ""} onChange={set("filing_date")} /></Field>
            <Field label="Case Status">
              <Select value={form.status} onValueChange={set("status")}>
                <SelectTrigger data-testid="case-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>{CASE_STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          </Section>

          <Section title="Client & Advocate">
            <Field label="Client">
              <Select value={form.client_id || ""} onValueChange={set("client_id")}>
                <SelectTrigger data-testid="case-client-select"><SelectValue placeholder="Select client" /></SelectTrigger>
                <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Assigned Advocate">
              <Select value={form.advocate_id || ""} onValueChange={set("advocate_id")}>
                <SelectTrigger data-testid="case-advocate-select"><SelectValue placeholder="Select advocate" /></SelectTrigger>
                <SelectContent>{advocates.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          </Section>

          <Section title="Opposite Party">
            <Field label="Name"><Input value={form.opposite_party_name} onChange={set("opposite_party_name")} /></Field>
            <Field label="Contact"><Input value={form.opposite_party_contact} onChange={set("opposite_party_contact")} /></Field>
            <Field label="Address" full><Input value={form.opposite_party_address} onChange={set("opposite_party_address")} /></Field>
          </Section>

          <Section title="Case Details">
            <Field label="Description" full><Textarea value={form.description} onChange={set("description")} rows={2} /></Field>
            <Field label="Important Facts" full><Textarea value={form.important_facts} onChange={set("important_facts")} rows={2} /></Field>
            <Field label="Legal Sections"><Input value={form.legal_sections} onChange={set("legal_sections")} /></Field>
            <Field label="Remarks"><Input value={form.remarks} onChange={set("remarks")} /></Field>
          </Section>

          <Section title="Next Hearing">
            <Field label="Date"><Input type="date" data-testid="case-hearing-date-input" value={form.next_hearing_date || ""} onChange={set("next_hearing_date")} /></Field>
            <Field label="Time"><Input type="time" value={form.next_hearing_time || ""} onChange={set("next_hearing_time")} /></Field>
            <Field label="Purpose"><Input value={form.hearing_purpose} onChange={set("hearing_purpose")} placeholder="Arguments / Evidence" /></Field>
            <Field label="Court Room / Bench"><Input value={form.court_room} onChange={set("court_room")} /></Field>
            <Field label="Notes" full><Textarea value={form.hearing_notes} onChange={set("hearing_notes")} rows={2} /></Field>
          </Section>
        </div>

        <DialogFooter className="gap-2">
          {!editing && <Button variant="outline" onClick={() => save(true)} disabled={saving} data-testid="case-save-draft-btn">Save as Draft</Button>}
          <Button onClick={() => save(false)} disabled={saving} data-testid="case-save-btn" className="bg-navy text-white hover:bg-navy-light">
            {saving ? "Saving…" : editing ? "Update Case" : "Create Case"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children, full }) {
  return (
    <div className={full ? "space-y-1.5 sm:col-span-2" : "space-y-1.5"}>
      <Label className="text-xs text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}
